import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { 
    FileText, Play, Clock, X, CheckCircle, Send, 
    BarChart2, Plus, Trash2, ChevronRight, Activity,
    ClipboardIcon, LayoutDashboard, History,
    CircleDot, CheckSquare, ListFilter, Type, AlignLeft,
    Sliders, ToggleLeft, ToggleRight, Copy, ChevronUp, ChevronDown,
    Eye, Edit3, Calendar, Sparkles, HelpCircle, Check, Tag
} from 'lucide-react';
import { Input } from '../../components/ui/Input';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';

export const QUESTION_TYPES = [
    { id: 'multiple_choice', label: 'Multiple choice', icon: CircleDot, description: 'Single select radio options' },
    { id: 'checkbox', label: 'Checkboxes', icon: CheckSquare, description: 'Multi-select checkboxes' },
    { id: 'dropdown', label: 'Dropdown', icon: ListFilter, description: 'Single select dropdown list' },
    { id: 'short_answer', label: 'Short answer', icon: Type, description: 'Single-line text response' },
    { id: 'paragraph', label: 'Paragraph', icon: AlignLeft, description: 'Multi-line detailed text' },
    { id: 'rating', label: 'Linear scale', icon: Sliders, description: 'Numeric rating scale (1-5 or 1-10)' },
    { id: 'boolean', label: 'Yes / No', icon: ToggleLeft, description: 'Binary choice' },
    { id: 'date', label: 'Date', icon: Calendar, description: 'Date selection picker' },
];

export const createNewQuestion = (type = 'multiple_choice', title = '') => ({
    id: `q_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    title,
    description: '',
    showDescription: false,
    type,
    required: true,
    options: ['multiple_choice', 'checkbox', 'dropdown'].includes(type)
        ? ['Option 1', 'Option 2']
        : [],
    hasOther: false,
    scaleMin: 1,
    scaleMax: 5,
    scaleMinLabel: 'Strongly Disagree / Poor',
    scaleMaxLabel: 'Strongly Agree / Excellent'
});

const PRESET_TEMPLATES = [
    {
        name: 'ADHD & Focus Screening',
        title: 'ADHD & Attention Observation Protocol',
        category: 'Behavioral & Attention',
        duration: '~10 mins',
        description: 'Comprehensive screening for sustained attention, impulse control, and hyperactivity markers.',
        questions: [
            {
                id: 'adhd_1',
                title: 'How often does the patient struggle to sustain attention in tasks or play?',
                description: 'Consider typical daily observations over the last 14 days.',
                showDescription: true,
                type: 'multiple_choice',
                required: true,
                options: ['Never / Rarely', 'Sometimes (1-2 times/wk)', 'Often (Almost daily)', 'Constantly / Severe'],
                hasOther: false
            },
            {
                id: 'adhd_2',
                title: 'Environments where inattention or restlessness is most evident:',
                description: 'Select all that apply.',
                showDescription: true,
                type: 'checkbox',
                required: true,
                options: ['Classroom / Academic', 'Home / Chores', 'Group Activities & Sports', 'Public & Social Settings'],
                hasOther: true
            },
            {
                id: 'adhd_3',
                title: 'Rate overall impulse control during unstructured transitions:',
                description: '1 indicates high impulsivity; 5 indicates age-appropriate self-control.',
                showDescription: true,
                type: 'rating',
                required: true,
                scaleMin: 1,
                scaleMax: 5,
                scaleMinLabel: 'Severe Impulsivity',
                scaleMaxLabel: 'Excellent Control'
            },
            {
                id: 'adhd_4',
                title: 'Does the patient experience significant sleep resistance or night awakenings?',
                description: '',
                showDescription: false,
                type: 'boolean',
                required: true,
                options: ['Yes', 'No']
            },
            {
                id: 'adhd_5',
                title: 'Therapist / Caregiver contextual observations:',
                description: 'Include any specific triggers or effective de-escalation strategies noted.',
                showDescription: true,
                type: 'paragraph',
                required: false,
                options: []
            }
        ]
    },
    {
        name: 'Daily Mood & Behavioral Tracker',
        title: 'Daily Mood & Emotion Regulation Log',
        category: 'Emotional Health',
        duration: '~5 mins',
        description: 'Quick check-in on emotional balance, frustration tolerance, and cooperative engagement.',
        questions: [
            {
                id: 'mood_1',
                title: 'Primary emotional presentation today:',
                description: '',
                showDescription: false,
                type: 'multiple_choice',
                required: true,
                options: ['Calm & Receptive', 'Anxious / Fidgety', 'Irritable / Frustrated', 'Withdrawn / Low Energy'],
                hasOther: true
            },
            {
                id: 'mood_2',
                title: 'Rate frustration tolerance when a planned routine was altered:',
                description: '1 indicates meltdown/outburst; 5 indicates calm adaptation.',
                showDescription: true,
                type: 'rating',
                required: true,
                scaleMin: 1,
                scaleMax: 5,
                scaleMinLabel: 'Meltdown / Outburst',
                scaleMaxLabel: 'Adapted Calmly'
            },
            {
                id: 'mood_3',
                title: 'Completed assigned daily milestones without physical assistance?',
                description: '',
                showDescription: false,
                type: 'boolean',
                required: true,
                options: ['Yes', 'No']
            },
            {
                id: 'mood_4',
                title: 'Specific breakthrough or challenge observed today:',
                description: '',
                showDescription: false,
                type: 'paragraph',
                required: false,
                options: []
            }
        ]
    }
];

export default function Assessments() {
    const { profiles, assessments, assignAssessment, adminEvaluations, patientQuestionnaires, addPatientQuestionnaire } = useData();
    const { user } = useAuth();
    const navigate = useNavigate();

    // Clinical Patient Modal
    const [isPatientModalOpen, setPatientModalOpen] = useState(false);
    const [selectedClinicalAssessment, setSelectedClinicalAssessment] = useState(null);
    const [isAssignModalOpen, setAssignModalOpen] = useState(false);
    const [selectedPatientId, setSelectedPatientId] = useState('');
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState('');
    const [selectedFrequency, setSelectedFrequency] = useState('Monthly');
    const [showSuccess, setShowSuccess] = useState(false);

    // Merge dynamic assessments with static history for demo
    const RECENT_HISTORY = [
        ...assessments.map(a => ({
            id: a.id,
            student: a.patientName,
            assessment: a.type,
            date: new Date(a.date).toLocaleDateString(),
            score: typeof a.score === 'number' ? `${Math.round((a.score / (a.questions?.length * 4 || 20)) * 100)}%` : 'Completed',
            submitterRole: a.submitterRole
        })).reverse(),
        { id: 'h1', student: 'Alex Johnson', assessment: 'Quarterly Progress Check', date: 'Oct 14, 2026', score: '78%', submitterRole: 'Patient' },
        { id: 'h2', student: 'Sophia Davis', assessment: 'Early Intervention Screener', date: 'Oct 12, 2026', score: 'Pending', submitterRole: 'Admin' },
    ];

    // Filter profiles for the current therapist if applicable
    const assignedProfiles = profiles.filter(p => !user || user.role === 'Admin' || p.assignedTherapistId === user.id);

    const handleStartClinical = (assessment) => {
        setSelectedClinicalAssessment(assessment);
        setPatientModalOpen(true);
    };

    const confirmClinicalStart = (patient) => {
        navigate('/Screening_test/app/assessments/take', { state: { assessment: selectedClinicalAssessment, patient } });
    };

    const openAssignModal = (preSelectId = '') => {
        setSelectedPatientId('');
        setSelectedQuestionnaireId(preSelectId);
        setShowSuccess(false);
        setAssignModalOpen(true);
    };

    const handleAssignToPortal = () => {
        if (!selectedPatientId || !selectedQuestionnaireId) return alert('Please select both patient and questionnaire.');
        const patient = profiles.find(p => p.id.toString() === selectedPatientId);
        const q = patientQuestionnaires.find(q => q.id === selectedQuestionnaireId);
        assignAssessment({
            patientId: patient.id,
            patientName: patient.name,
            assessmentTitle: q.title,
            domains: q.domains,
            duration: q.duration,
            questions: q.questions,
            frequency: selectedFrequency
        });
        setShowSuccess(true);
        setTimeout(() => { setShowSuccess(false); setAssignModalOpen(false); }, 2000);
    };

    // Create questionnaire modal state (Google Forms Style)
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [createTitle, setCreateTitle] = useState('');
    const [createCategory, setCreateCategory] = useState('Behavioral Screener');
    const [createDuration, setCreateDuration] = useState('~10 mins');
    const [createDescription, setCreateDescription] = useState('');
    const [createQuestions, setCreateQuestions] = useState([
        createNewQuestion('multiple_choice', 'How would you rate the patient’s engagement and focus today?'),
        createNewQuestion('rating', 'Overall developmental milestone progress level:'),
    ]);
    const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [previewAnswers, setPreviewAnswers] = useState({});
    const [createSuccess, setCreateSuccess] = useState(false);
    const [lastCreatedId, setLastCreatedId] = useState(null);

    // Question manipulation handlers
    const handleAddQuestion = (type = 'multiple_choice') => {
        const newQ = createNewQuestion(type);
        setCreateQuestions(prev => [...prev, newQ]);
        setActiveQuestionIndex(createQuestions.length);
    };

    const handleDuplicateQuestion = (idx) => {
        const sourceQ = createQuestions[idx];
        const duplicatedQ = {
            ...sourceQ,
            id: `q_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
            title: sourceQ.title ? `${sourceQ.title} (Copy)` : 'Copy of Question',
            options: Array.isArray(sourceQ.options) ? [...sourceQ.options] : []
        };
        const updated = [...createQuestions];
        updated.splice(idx + 1, 0, duplicatedQ);
        setCreateQuestions(updated);
        setActiveQuestionIndex(idx + 1);
    };

    const handleRemoveQuestion = (idx) => {
        if (createQuestions.length <= 1) {
            alert('A template must contain at least one question.');
            return;
        }
        setCreateQuestions(prev => prev.filter((_, i) => i !== idx));
        setActiveQuestionIndex(prev => Math.max(0, Math.min(prev, createQuestions.length - 2)));
    };

    const handleMoveQuestion = (idx, direction) => {
        const targetIdx = idx + direction;
        if (targetIdx < 0 || targetIdx >= createQuestions.length) return;
        const updated = [...createQuestions];
        const [moved] = updated.splice(idx, 1);
        updated.splice(targetIdx, 0, moved);
        setCreateQuestions(updated);
        setActiveQuestionIndex(targetIdx);
    };

    const handleQuestionFieldChange = (idx, field, val) => {
        setCreateQuestions(prev => prev.map((q, i) => {
            if (i !== idx) return q;
            if (field === 'type') {
                const needsOptions = ['multiple_choice', 'checkbox', 'dropdown'].includes(val);
                const currentOptions = Array.isArray(q.options) && q.options.length > 0 ? q.options : ['Option 1', 'Option 2'];
                return {
                    ...q,
                    type: val,
                    options: needsOptions ? currentOptions : []
                };
            }
            return { ...q, [field]: val };
        }));
    };

    const handleAddOption = (qIdx) => {
        setCreateQuestions(prev => prev.map((q, i) => {
            if (i !== qIdx) return q;
            const currentOpts = Array.isArray(q.options) ? q.options : [];
            return {
                ...q,
                options: [...currentOpts, `Option ${currentOpts.length + 1}`]
            };
        }));
    };

    const handleOptionChange = (qIdx, optIdx, val) => {
        setCreateQuestions(prev => prev.map((q, i) => {
            if (i !== qIdx) return q;
            const updatedOpts = [...q.options];
            updatedOpts[optIdx] = val;
            return { ...q, options: updatedOpts };
        }));
    };

    const handleRemoveOption = (qIdx, optIdx) => {
        setCreateQuestions(prev => prev.map((q, i) => {
            if (i !== qIdx) return q;
            if (q.options.length <= 1) return q;
            return {
                ...q,
                options: q.options.filter((_, oIdx) => oIdx !== optIdx)
            };
        }));
    };

    const handleToggleOther = (qIdx) => {
        setCreateQuestions(prev => prev.map((q, i) => {
            if (i !== qIdx) return q;
            return { ...q, hasOther: !q.hasOther };
        }));
    };

    const handleLoadPreset = (preset) => {
        setCreateTitle(preset.title);
        setCreateCategory(preset.category);
        setCreateDuration(preset.duration);
        setCreateDescription(preset.description);
        setCreateQuestions(preset.questions.map(q => ({
            ...q,
            id: `q_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
            options: Array.isArray(q.options) ? [...q.options] : []
        })));
        setActiveQuestionIndex(0);
    };

    const handleCreateSubmit = () => {
        if (!createTitle.trim()) {
            alert('Please enter a protocol title.');
            return;
        }

        // Validate and clean questions
        const sanitizedQuestions = createQuestions.map((q, idx) => {
            const rawTitle = (q.title || '').trim();
            const title = rawTitle || `Question #${idx + 1}`;
            const type = q.type || 'multiple_choice';

            // Clean options for choice types
            let options = [];
            if (['multiple_choice', 'checkbox', 'dropdown'].includes(type)) {
                options = (Array.isArray(q.options) ? q.options : [])
                    .map(o => (typeof o === 'string' ? o.trim() : ''))
                    .filter(Boolean);
                if (options.length === 0) {
                    options = ['Option 1', 'Option 2'];
                }
            } else if (type === 'boolean') {
                options = ['Yes', 'No'];
            }

            return {
                id: q.id || `q_${Date.now()}_${idx}`,
                title,
                description: (q.description || '').trim(),
                type,
                required: q.required !== false,
                options,
                hasOther: !!q.hasOther,
                scaleMin: Number(q.scaleMin) || 1,
                scaleMax: Number(q.scaleMax) || 5,
                scaleMinLabel: (q.scaleMinLabel || '').trim() || 'Minimum',
                scaleMaxLabel: (q.scaleMaxLabel || '').trim() || 'Maximum'
            };
        });

        if (sanitizedQuestions.length === 0) {
            alert('Please add at least one question.');
            return;
        }

        const newQ = addPatientQuestionnaire({
            title: createTitle.trim(),
            category: createCategory.trim() || 'Clinical Protocol',
            description: createDescription.trim(),
            duration: createDuration.trim() || '~10 mins',
            questions: sanitizedQuestions,
        });

        setLastCreatedId(newQ.id);
        setCreateSuccess(true);
        setTimeout(() => {
            setCreateSuccess(false);
            setCreateModalOpen(false);
            setIsPreviewMode(false);
        }, 1800);
    };

    return (
        <div className="space-y-10">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 font-outfit tracking-tight">Evaluations</h1>
                    <p className="text-slate-500 mt-2 text-lg font-light">Conduct clinical checks or dispense forms to patient portals.</p>
                </div>
                <div className="flex gap-3">
                     <Button variant="outline" onClick={() => setCreateModalOpen(true)} className="rounded-2xl h-14 px-6 border-slate-200 bg-white font-black text-xs uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all">
                        <Plus className="mr-2 h-4 w-4" /> New Form Template
                    </Button>
                    <Button onClick={() => openAssignModal()} className="rounded-2xl h-14 px-8 shadow-premium font-black text-xs uppercase tracking-widest bg-brand-600 hover:bg-brand-700 text-white transition-all">
                        <Send className="mr-3 h-4 w-4" /> Dispatch Form
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
                <div className="xl:col-span-8 space-y-12">
                    
                    {/* Clinical Evaluations (In-Clinic) */}
                    <div>
                        <div className="flex items-center gap-4 mb-8">
                             <div className="h-12 w-12 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-600 border border-brand-100 shadow-sm">
                                <LayoutDashboard className="h-6 w-6" />
                             </div>
                             <div>
                                <h2 className="text-2xl font-black text-slate-900 font-outfit tracking-tight">Clinical Assessments by Therapist</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Available: {adminEvaluations.length}</p>
                             </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-8">
                            {adminEvaluations.map(item => (
                                <Card key={item.id} className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 hover:shadow-premium hover:-translate-y-1 transition-all group overflow-hidden">
                                    <div className="p-8 h-full flex flex-col">
                                        <div className="mb-6 flex justify-between items-start">
                                            <Badge className="bg-slate-50 text-slate-400 border-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl">{item.type}</Badge>
                                             <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-200 group-hover:bg-brand-50 group-hover:text-brand-400 transition-all">
                                                <ClipboardIcon className="h-5 w-5" />
                                             </div>
                                        </div>
                                        <h3 className="text-xl font-black text-slate-900 group-hover:text-brand-600 transition-colors uppercase tracking-tight font-outfit mb-3">{item.title}</h3>
                                        <div className="flex items-center gap-4 text-[10px] font-black uppercase text-slate-400 tracking-widest mb-10">
                                            <span className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> {item.questions?.length??1} Tests Available</span>
                                            <span className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> {item.domains} Domains</span>
                                            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {item.duration}</span>
                                        </div>
                                        <Button 
                                            className="w-full h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-[0.2em] shadow-lg transition-all"
                                            onClick={() => handleStartClinical(item)}
                                        >
                                            Initialize Test <Play className="ml-3 h-3 w-3 fill-current" />
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>

                    {/* At-Home Questionnaires */}
                    <div>
                        <div className="flex items-center gap-4 mb-8">
                             <div className="h-12 w-12 rounded-2xl bg-accent-50 flex items-center justify-center text-accent-600 border border-accent-100 shadow-sm">
                                <Send className="h-6 w-6" />
                             </div>
                             <div>
                                <h2 className="text-2xl font-black text-slate-900 font-outfit tracking-tight">Dispensed Assessment Protocols for Patient</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">Dispensed to Patient Portals: Available {patientQuestionnaires.length}</p>
                             </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-8">
                            {patientQuestionnaires.map(item => (
                                <Card key={item.id} className={`border-0 shadow-soft rounded-[2.5rem] bg-white border transition-all group overflow-hidden ${item.id === lastCreatedId ? 'ring-2 ring-brand-500 border-transparent' : 'border-slate-50'}`}>
                                    <div className="p-8 h-full flex flex-col">
                                        <div className="mb-6 flex justify-between items-start">
                                            <Badge className="bg-slate-50 text-slate-400 border-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl">Portal Release</Badge>
                                             {item.id === lastCreatedId && <Badge className="bg-brand-600 text-white border-0 shadow-sm px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl">New</Badge>}
                                        </div>
                                        <h3 className="text-xl font-black text-slate-900 group-hover:text-brand-600 transition-colors uppercase tracking-tight font-outfit mb-3">{item.title}</h3>
                                        <div className="flex items-center gap-4 text-[10px] font-black uppercase text-slate-400 tracking-widest mb-10">
                                            <span className="flex items-center gap-1.5 font-black">{item.questions.length} Questions</span>
                                            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {item.duration}</span>
                                        </div>
                                        <Button 
                                            variant="outline" 
                                            className="w-full h-14 rounded-2xl border-slate-200 text-slate-600 hover:bg-slate-50 font-black text-xs uppercase tracking-[0.2em] transition-all"
                                            onClick={() => openAssignModal(item.id)}
                                        >
                                            Assign to Patient <ChevronRight className="ml-3 h-4 w-4" />
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column: History */}
                <div className="xl:col-span-4 space-y-10">
                    <div className="flex items-center gap-4 mb-4">
                         <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center text-brand-400 shadow-sm">
                            <History className="h-5 w-5" />
                         </div>
                         <h2 className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Audit Trail</h2>
                    </div>

                    <Card className="border-0 shadow-soft rounded-[2.5rem] overflow-hidden bg-white border border-slate-50">
                        <CardContent className="p-0">
                            <div className="divide-y divide-slate-50">
                                {RECENT_HISTORY.slice(0, 10).map((hist, idx) => (
                                    <div key={idx} className="p-6 hover:bg-slate-50/50 transition-all group flex flex-col gap-4">
                                        <div className="flex justify-between items-center">
                                            <Badge className={`rounded-lg px-2 py-0.5 text-[8px] font-black uppercase tracking-tighter ${hist.submitterRole === 'Patient' ? 'bg-teal-50 text-teal-600' : 'bg-brand-50 text-brand-600'}`}>
                                                Origin: {hist.submitterRole || 'Clinical'}
                                            </Badge>
                                            <span className="text-[10px] font-bold text-slate-300">{hist.date}</span>
                                        </div>
                                        <div>
                                            <p className="font-black text-slate-900 text-sm tracking-tight font-outfit uppercase">{hist.student}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{hist.assessment}</p>
                                        </div>
                                        <div className="flex items-center justify-between mt-2">
                                            <div className="flex items-center gap-2">
                                                <div className={`h-2 w-2 rounded-full ${hist.score === 'Pending' ? 'bg-amber-400' : 'bg-green-500'}`}></div>
                                                <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{hist.score}</span>
                                            </div>
                                            {(hist.submitterRole === 'Patient' && hist.id && hist.id !== 'h1') && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm" 
                                                    className="h-8 rounded-xl px-3 text-[10px] font-black uppercase tracking-widest text-brand-600 hover:bg-brand-50" 
                                                    onClick={() => navigate(`/Screening_test/app/assessments/analysis/${hist.id}`)}
                                                >
                                                    Analysis <BarChart2 className="ml-1.5 h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Assign Modal */}
            {isAssignModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[100] flex items-center justify-center p-6">
                    <Card className="w-full max-w-xl shadow-premium border-0 rounded-[3rem] overflow-hidden bg-white">
                        <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h2 className="text-3xl font-black text-slate-900 font-outfit tracking-tight uppercase">Dispense Form</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Release secure evaluation protocol to portal</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setAssignModalOpen(false)} className="h-10 w-10 bg-white shadow-sm border border-slate-100 rounded-xl hover:text-red-500 transition-colors"><X className="h-5 w-5" /></Button>
                        </div>

                        {showSuccess ? (
                            <div className="p-20 text-center">
                                <div className="mx-auto w-24 h-24 bg-brand-50 text-brand-600 rounded-[2.5rem] flex items-center justify-center mb-10 shadow-inner">
                                    <CheckCircle className="h-12 w-12" />
                                </div>
                                <h3 className="text-3xl font-black text-slate-900 font-outfit uppercase tracking-tight">Form Dispensed</h3>
                                <p className="text-slate-400 font-medium mt-4">The clinical portal has updated the patient view.</p>
                            </div>
                        ) : (
                            <div className="p-10 space-y-10">
                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block px-1">1. Clinical Profile</label>
                                    <select
                                        className="w-full h-16 rounded-[1.5rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 px-6 transition-all"
                                        value={selectedPatientId}
                                        onChange={(e) => setSelectedPatientId(e.target.value)}
                                    >
                                        <option value="" disabled>Select active profile...</option>
                                        {profiles.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block px-1">2. Target Architecture</label>
                                    <select
                                        className="w-full h-16 rounded-[1.5rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 px-6 transition-all"
                                        value={selectedQuestionnaireId}
                                        onChange={(e) => setSelectedQuestionnaireId(e.target.value)}
                                    >
                                        <option value="" disabled>Choose protocol...</option>
                                        {patientQuestionnaires.map(q => (
                                            <option key={q.id} value={q.id}>{q.title}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block px-1">3. Clinical Frequency</label>
                                    <select
                                        className="w-full h-16 rounded-[1.5rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 px-6 transition-all"
                                        value={selectedFrequency}
                                        onChange={(e) => setSelectedFrequency(e.target.value)}
                                    >
                                        {['Daily', 'Weekly', 'Monthly', 'Annually'].map(f => (
                                            <option key={f} value={f}>{f}</option>
                                        ))}
                                    </select>
                                </div>

                                <Button 
                                    className="w-full h-16 rounded-[1.5rem] bg-brand-600 hover:bg-brand-700 text-white font-black text-sm uppercase tracking-widest shadow-premium transition-all mt-4" 
                                    onClick={handleAssignToPortal}
                                >
                                    Confirm Dispatch <Send className="ml-3 h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
            )}

            {/* Create Template Modal (Google Forms-Style Builder) */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-xl z-[100] flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-300">
                    <Card className="w-full max-w-4xl shadow-premium border-0 rounded-[2.5rem] overflow-hidden bg-slate-100/90 backdrop-blur-2xl max-h-[92vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="p-6 sm:p-8 border-b border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white flex-shrink-0">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <Badge className="bg-brand-50 text-brand-700 border-brand-200 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-lg">
                                        Form Studio
                                    </Badge>
                                    <span className="text-slate-400 text-xs font-bold">•</span>
                                    <span className="text-slate-400 text-xs font-semibold">{createQuestions.length} Questions</span>
                                </div>
                                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 font-outfit tracking-tight uppercase">
                                    Template Builder
                                </h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-0.5">
                                    Google Forms-style protocol designer for clinical & patient release
                                </p>
                            </div>

                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                {/* Preset templates quick-load */}
                                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-2xl p-1">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2 flex items-center gap-1">
                                        <Sparkles className="h-3 w-3 text-brand-500" /> Presets:
                                    </span>
                                    {PRESET_TEMPLATES.map((preset, pIdx) => (
                                        <button
                                            key={pIdx}
                                            onClick={() => handleLoadPreset(preset)}
                                            className="px-2.5 py-1 rounded-xl text-[10px] font-bold text-slate-600 hover:text-brand-600 hover:bg-white transition-all"
                                            title={`Load ${preset.name}`}
                                        >
                                            {pIdx === 0 ? 'ADHD' : 'Mood'}
                                        </button>
                                    ))}
                                </div>

                                {/* Preview vs Editor Toggle */}
                                <div className="flex bg-slate-100 rounded-2xl p-1 border border-slate-200">
                                    <button
                                        type="button"
                                        onClick={() => setIsPreviewMode(false)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                                            !isPreviewMode ? 'bg-white text-brand-600 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                    >
                                        <Edit3 className="h-3.5 w-3.5" /> Editor
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsPreviewMode(true)}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                                            isPreviewMode ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'
                                        }`}
                                    >
                                        <Eye className="h-3.5 w-3.5" /> Preview
                                    </button>
                                </div>

                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    onClick={() => setCreateModalOpen(false)} 
                                    className="h-10 w-10 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-2xl hover:text-red-500 transition-colors"
                                >
                                    <X className="h-5 w-5" />
                                </Button>
                            </div>
                        </div>

                        {createSuccess ? (
                            <div className="p-20 text-center bg-white flex-1 flex flex-col items-center justify-center">
                                <div className="mx-auto w-24 h-24 bg-green-50 text-green-600 rounded-[2.5rem] flex items-center justify-center mb-8 shadow-inner animate-in zoom-in-75">
                                    <CheckCircle className="h-12 w-12" />
                                </div>
                                <h3 className="text-3xl font-black text-slate-900 font-outfit uppercase tracking-tight">Template Finalized & Saved!</h3>
                                <p className="text-slate-400 font-medium mt-3 max-w-md">
                                    "{createTitle}" has been packaged with {createQuestions.length} Google Form question(s) and is now ready in your protocol library.
                                </p>
                            </div>
                        ) : isPreviewMode ? (
                            /* LIVE FORM PREVIEW MODE (Google Forms preview) */
                            <div className="flex-1 overflow-y-auto p-6 sm:p-10 space-y-8 custom-scrollbar bg-slate-100">
                                <div className="bg-gradient-to-r from-brand-600 via-accent-500 to-indigo-600 p-4 rounded-3xl text-white flex items-center justify-between shadow-md">
                                    <div className="flex items-center gap-3">
                                        <Eye className="h-5 w-5" />
                                        <p className="text-xs font-black uppercase tracking-widest">
                                            Interactive Live Preview Mode
                                        </p>
                                    </div>
                                    <button 
                                        onClick={() => setIsPreviewMode(false)}
                                        className="bg-white/20 hover:bg-white/30 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl transition-all"
                                    >
                                        Return to Editor
                                    </button>
                                </div>

                                {/* Preview Form Header Card */}
                                <div className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm relative overflow-hidden">
                                    <div className="absolute top-0 left-0 right-0 h-2.5 bg-gradient-to-r from-brand-600 via-accent-500 to-indigo-600" />
                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        <Badge className="bg-brand-50 text-brand-700 border-brand-200 px-3 py-1 text-[10px] font-black uppercase tracking-wider rounded-xl">
                                            {createCategory || 'Clinical Protocol'}
                                        </Badge>
                                        <span className="text-slate-400 text-xs font-bold">•</span>
                                        <span className="text-slate-500 text-xs font-bold flex items-center gap-1">
                                            <Clock className="h-3.5 w-3.5 text-slate-400" /> {createDuration || '~10 mins'}
                                        </span>
                                    </div>
                                    <h1 className="text-3xl font-black text-slate-900 font-outfit uppercase tracking-tight">
                                        {createTitle || 'Untitled Clinical Protocol'}
                                    </h1>
                                    <p className="text-slate-500 text-sm mt-3 leading-relaxed">
                                        {createDescription || 'Complete this evaluation protocol by responding to the questions below.'}
                                    </p>
                                    <div className="mt-4 pt-4 border-t border-slate-100 text-[11px] font-bold text-red-500 uppercase tracking-wider">
                                        * Indicates required question
                                    </div>
                                </div>

                                {/* Preview Questions */}
                                <div className="space-y-6">
                                    {createQuestions.map((q, idx) => (
                                        <div key={q.id || idx} className="bg-white rounded-3xl p-8 border border-slate-200/80 shadow-sm space-y-6">
                                            <div>
                                                <div className="flex items-start justify-between gap-4">
                                                    <h3 className="text-lg font-black text-slate-900 font-outfit">
                                                        <span className="text-brand-600 mr-2">Q{idx + 1}.</span>
                                                        {q.title || `Untitled Question #${idx + 1}`}
                                                        {q.required && <span className="text-red-500 ml-1.5 font-black">*</span>}
                                                    </h3>
                                                    <Badge className="bg-slate-50 text-slate-500 border-slate-200 text-[9px] font-black uppercase tracking-widest shrink-0 rounded-lg">
                                                        {QUESTION_TYPES.find(t => t.id === q.type)?.label || q.type}
                                                    </Badge>
                                                </div>
                                                {q.description && (
                                                    <p className="text-xs text-slate-400 font-medium mt-1.5 leading-relaxed">
                                                        {q.description}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Preview controls based on type */}
                                            {q.type === 'multiple_choice' && (
                                                <div className="space-y-3">
                                                    {(q.options || ['Option 1', 'Option 2']).map((opt, oIdx) => (
                                                        <label 
                                                            key={oIdx} 
                                                            className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                                                                previewAnswers[q.id] === opt 
                                                                    ? 'border-brand-600 bg-brand-50/60 text-brand-900 font-bold' 
                                                                    : 'border-slate-100 hover:border-slate-200 text-slate-700 bg-slate-50/50'
                                                            }`}
                                                            onClick={() => setPreviewAnswers(prev => ({ ...prev, [q.id]: opt }))}
                                                        >
                                                            <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                                                                previewAnswers[q.id] === opt ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
                                                            }`}>
                                                                {previewAnswers[q.id] === opt && <div className="h-2 w-2 rounded-full bg-white" />}
                                                            </div>
                                                            <span className="text-sm font-medium">{opt}</span>
                                                        </label>
                                                    ))}
                                                    {q.hasOther && (
                                                        <label 
                                                            className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                                                                previewAnswers[q.id] === '__other__' ? 'border-brand-600 bg-brand-50/60 font-bold' : 'border-slate-100 bg-slate-50/50'
                                                            }`}
                                                            onClick={() => setPreviewAnswers(prev => ({ ...prev, [q.id]: '__other__' }))}
                                                        >
                                                            <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                                                                previewAnswers[q.id] === '__other__' ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
                                                            }`}>
                                                                {previewAnswers[q.id] === '__other__' && <div className="h-2 w-2 rounded-full bg-white" />}
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-500">Other:</span>
                                                            <input 
                                                                type="text" 
                                                                placeholder="Your custom response..." 
                                                                className="flex-1 bg-transparent border-b border-slate-300 text-sm py-1 focus:outline-none focus:border-brand-600"
                                                                disabled={previewAnswers[q.id] !== '__other__'}
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                            )}

                                            {q.type === 'checkbox' && (
                                                <div className="space-y-3">
                                                    {(q.options || ['Option 1', 'Option 2']).map((opt, oIdx) => {
                                                        const currentArr = Array.isArray(previewAnswers[q.id]) ? previewAnswers[q.id] : [];
                                                        const isChecked = currentArr.includes(opt);
                                                        return (
                                                            <label 
                                                                key={oIdx} 
                                                                className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                                                                    isChecked 
                                                                        ? 'border-brand-600 bg-brand-50/60 text-brand-900 font-bold' 
                                                                        : 'border-slate-100 hover:border-slate-200 text-slate-700 bg-slate-50/50'
                                                                }`}
                                                                onClick={() => {
                                                                    setPreviewAnswers(prev => {
                                                                        const existing = Array.isArray(prev[q.id]) ? prev[q.id] : [];
                                                                        const next = existing.includes(opt) ? existing.filter(x => x !== opt) : [...existing, opt];
                                                                        return { ...prev, [q.id]: next };
                                                                    });
                                                                }}
                                                            >
                                                                <div className={`h-5 w-5 rounded-lg border-2 flex items-center justify-center ${
                                                                    isChecked ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300'
                                                                }`}>
                                                                    {isChecked && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                                                                </div>
                                                                <span className="text-sm font-medium">{opt}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {q.type === 'dropdown' && (
                                                <div>
                                                    <select 
                                                        value={previewAnswers[q.id] || ''} 
                                                        onChange={(e) => setPreviewAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                                        className="w-full h-14 rounded-2xl border-2 border-slate-200 bg-slate-50 px-5 text-sm font-bold text-slate-700 focus:bg-white focus:border-brand-600 outline-none"
                                                    >
                                                        <option value="">Choose an option...</option>
                                                        {(q.options || []).map((opt, oIdx) => (
                                                            <option key={oIdx} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {q.type === 'short_answer' && (
                                                <Input 
                                                    placeholder="Short-answer text..."
                                                    value={previewAnswers[q.id] || ''}
                                                    onChange={(e) => setPreviewAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                                    className="h-14 rounded-2xl bg-slate-50 border-2 border-slate-100 px-5 text-sm font-medium focus:bg-white focus:border-brand-600"
                                                />
                                            )}

                                            {q.type === 'paragraph' && (
                                                <textarea 
                                                    placeholder="Long-answer text / clinical remarks..."
                                                    value={previewAnswers[q.id] || ''}
                                                    onChange={(e) => setPreviewAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                                    rows={4}
                                                    className="w-full rounded-2xl bg-slate-50 border-2 border-slate-100 p-5 text-sm font-medium focus:bg-white focus:border-brand-600 outline-none"
                                                />
                                            )}

                                            {q.type === 'rating' && (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-widest px-1">
                                                        <span>{q.scaleMinLabel || 'Minimum (1)'}</span>
                                                        <span>{q.scaleMaxLabel || `Maximum (${q.scaleMax || 5})`}</span>
                                                    </div>
                                                    <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                                                        {Array.from({ length: q.scaleMax || 5 }, (_, i) => i + 1).map(num => (
                                                            <button
                                                                key={num}
                                                                type="button"
                                                                onClick={() => setPreviewAnswers(prev => ({ ...prev, [q.id]: num }))}
                                                                className={`h-12 w-12 sm:h-14 sm:w-14 rounded-2xl font-black text-sm sm:text-base transition-all border-2 flex items-center justify-center ${
                                                                    previewAnswers[q.id] === num
                                                                        ? 'bg-brand-600 border-brand-600 text-white shadow-premium scale-105'
                                                                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-brand-300 hover:bg-white'
                                                                }`}
                                                            >
                                                                {num}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {q.type === 'boolean' && (
                                                <div className="grid grid-cols-2 gap-4">
                                                    {['Yes', 'No'].map(val => (
                                                        <button
                                                            key={val}
                                                            type="button"
                                                            onClick={() => setPreviewAnswers(prev => ({ ...prev, [q.id]: val }))}
                                                            className={`h-16 rounded-2xl font-black text-sm uppercase tracking-wider transition-all border-2 flex items-center justify-center gap-2 ${
                                                                previewAnswers[q.id] === val
                                                                    ? (val === 'Yes' ? 'bg-green-600 border-green-600 text-white shadow-premium' : 'bg-red-600 border-red-600 text-white shadow-premium')
                                                                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-brand-300'
                                                            }`}
                                                        >
                                                            {val} {previewAnswers[q.id] === val && <Check className="h-4 w-4" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {q.type === 'date' && (
                                                <Input 
                                                    type="date"
                                                    value={previewAnswers[q.id] || ''}
                                                    onChange={(e) => setPreviewAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                                    className="h-14 rounded-2xl bg-slate-50 border-2 border-slate-100 px-5 text-sm font-bold text-slate-800"
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsPreviewMode(false)}
                                        className="h-16 rounded-2xl border-slate-200 font-black text-xs uppercase tracking-widest px-8"
                                    >
                                        <Edit3 className="mr-2 h-4 w-4" /> Edit Template
                                    </Button>
                                    <Button
                                        onClick={handleCreateSubmit}
                                        className="flex-1 h-16 rounded-2xl bg-brand-600 hover:bg-brand-700 text-white font-black text-xs uppercase tracking-widest shadow-premium"
                                    >
                                        Save & Release Template <CheckCircle className="ml-2 h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            /* GOOGLE FORMS-STYLE EDITOR MODE */
                            <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar bg-slate-100">
                                {/* Form Top Settings Card */}
                                <div className="bg-white rounded-[2rem] p-6 sm:p-8 border border-slate-200 shadow-sm relative overflow-hidden space-y-6">
                                    <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-brand-600 via-accent-500 to-indigo-600" />
                                    
                                    <div className="space-y-4 pt-1">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                Protocol Title *
                                            </label>
                                            <Input 
                                                placeholder="e.g. Attention & Focus Observation Protocol" 
                                                value={createTitle}
                                                onChange={(e) => setCreateTitle(e.target.value)}
                                                className="h-16 rounded-2xl bg-slate-50 border-2 border-slate-100 font-black font-outfit text-xl sm:text-2xl text-slate-900 focus:bg-white focus:border-brand-600 shadow-none"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                    Clinical Category / Domain
                                                </label>
                                                <select
                                                    value={createCategory}
                                                    onChange={(e) => setCreateCategory(e.target.value)}
                                                    className="w-full h-12 rounded-xl bg-slate-50 border-2 border-slate-100 px-4 font-bold text-xs text-slate-700 focus:bg-white focus:border-brand-600 outline-none"
                                                >
                                                    <option value="Behavioral Screener">Behavioral Screener</option>
                                                    <option value="Executive Function">Executive Function & Focus</option>
                                                    <option value="Developmental Milestone">Developmental Milestone</option>
                                                    <option value="Emotional Health">Emotional Regulation & Mood</option>
                                                    <option value="Daily Progress Log">Daily Progress Log</option>
                                                    <option value="General Clinical Protocol">General Clinical Protocol</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                    Estimated Time
                                                </label>
                                                <Input 
                                                    placeholder="~10 mins" 
                                                    value={createDuration}
                                                    onChange={(e) => setCreateDuration(e.target.value)}
                                                    className="h-12 rounded-xl bg-slate-50 border-2 border-slate-100 font-bold text-xs"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                Protocol Description / Respondent Guidance (Optional)
                                            </label>
                                            <textarea 
                                                placeholder="Provide brief instructions or scoring context for patients and therapists..."
                                                value={createDescription}
                                                onChange={(e) => setCreateDescription(e.target.value)}
                                                rows={2}
                                                className="w-full rounded-xl bg-slate-50 border-2 border-slate-100 p-4 text-xs font-medium text-slate-700 focus:bg-white focus:border-brand-600 outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Questions List */}
                                <div className="space-y-5">
                                    {createQuestions.map((q, idx) => {
                                        const isActive = activeQuestionIndex === idx;
                                        return (
                                            <div 
                                                key={q.id || idx}
                                                onClick={() => setActiveQuestionIndex(idx)}
                                                className={`rounded-[2rem] p-6 sm:p-8 transition-all border-2 relative overflow-hidden ${
                                                    isActive 
                                                        ? 'bg-white border-brand-500 shadow-premium ring-4 ring-brand-50' 
                                                        : 'bg-white/80 border-slate-200/80 hover:border-slate-300 shadow-sm'
                                                }`}
                                            >
                                                {/* Active left indicator stripe */}
                                                {isActive && (
                                                    <div className="absolute top-0 bottom-0 left-0 w-2.5 bg-brand-600" />
                                                )}

                                                <div className="space-y-6">
                                                    {/* Top row: Item Index + Question Input + Question Type Selector */}
                                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                                        <div className="flex items-center gap-2 flex-1">
                                                            {/* Reorder Arrows */}
                                                            <div className="flex flex-col gap-0.5">
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); handleMoveQuestion(idx, -1); }}
                                                                    disabled={idx === 0}
                                                                    className="h-6 w-6 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-20"
                                                                    title="Move Up"
                                                                >
                                                                    <ChevronUp className="h-3.5 w-3.5" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); handleMoveQuestion(idx, 1); }}
                                                                    disabled={idx === createQuestions.length - 1}
                                                                    className="h-6 w-6 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-20"
                                                                    title="Move Down"
                                                                >
                                                                    <ChevronDown className="h-3.5 w-3.5" />
                                                                </button>
                                                            </div>

                                                            <Badge className="bg-slate-100 text-slate-700 border-0 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-xl shrink-0">
                                                                #{idx + 1}
                                                            </Badge>

                                                            {/* Question Prompt Title Input */}
                                                            <div className="flex-1">
                                                                <Input 
                                                                    placeholder={`Question #${idx + 1} prompt (e.g. How often does frustration occur?)`}
                                                                    value={q.title}
                                                                    onChange={(e) => handleQuestionFieldChange(idx, 'title', e.target.value)}
                                                                    className="h-14 rounded-2xl bg-slate-50 border-2 border-slate-100 font-bold text-slate-900 focus:bg-white focus:border-brand-600"
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Question Type Selector (Google Forms signature selector) */}
                                                        <div className="w-full lg:w-64 shrink-0">
                                                            <div className="relative">
                                                                <select
                                                                    value={q.type}
                                                                    onChange={(e) => handleQuestionFieldChange(idx, 'type', e.target.value)}
                                                                    className="w-full h-14 rounded-2xl border-2 border-slate-200 bg-slate-50 px-4 pr-10 text-xs font-black uppercase tracking-wider text-slate-800 focus:bg-white focus:border-brand-600 outline-none"
                                                                >
                                                                    {QUESTION_TYPES.map(t => (
                                                                        <option key={t.id} value={t.id}>
                                                                            {t.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                                                    <ListFilter className="h-4 w-4" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Optional Question Description */}
                                                    <div>
                                                        {q.showDescription || q.description ? (
                                                            <div className="flex items-center gap-2">
                                                                <Input 
                                                                    placeholder="Add clinical instructions or respondent guidance (optional)..."
                                                                    value={q.description || ''}
                                                                    onChange={(e) => handleQuestionFieldChange(idx, 'description', e.target.value)}
                                                                    className="h-10 rounded-xl bg-slate-50/70 border border-slate-200 text-xs text-slate-600"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        handleQuestionFieldChange(idx, 'description', '');
                                                                        handleQuestionFieldChange(idx, 'showDescription', false);
                                                                    }}
                                                                    className="h-10 px-2 text-slate-400 hover:text-red-500 text-xs font-bold"
                                                                    title="Remove description"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleQuestionFieldChange(idx, 'showDescription', true)}
                                                                className="text-[11px] font-bold text-brand-600 hover:text-brand-700 tracking-wide flex items-center gap-1"
                                                            >
                                                                <Plus className="h-3 w-3" /> Add question description / help text
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Question Type Body & Options Editor */}
                                                    <div className="pt-2">
                                                        {/* MULTIPLE CHOICE & CHECKBOX & DROPDOWN OPTIONS */}
                                                        {['multiple_choice', 'checkbox', 'dropdown'].includes(q.type) && (
                                                            <div className="space-y-3">
                                                                <div className="space-y-2.5">
                                                                    {(q.options || ['Option 1', 'Option 2']).map((opt, oIdx) => (
                                                                        <div key={oIdx} className="flex items-center gap-3">
                                                                            <div className="text-slate-400 shrink-0">
                                                                                {q.type === 'multiple_choice' && <CircleDot className="h-4 w-4" />}
                                                                                {q.type === 'checkbox' && <CheckSquare className="h-4 w-4" />}
                                                                                {q.type === 'dropdown' && <span className="text-xs font-black">{oIdx + 1}.</span>}
                                                                            </div>
                                                                            <Input 
                                                                                value={opt}
                                                                                onChange={(e) => handleOptionChange(idx, oIdx, e.target.value)}
                                                                                placeholder={`Option ${oIdx + 1}`}
                                                                                className="h-11 rounded-xl bg-slate-50 border border-slate-200 text-xs font-medium text-slate-800 focus:bg-white focus:border-brand-600"
                                                                            />
                                                                            {q.options?.length > 1 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => handleRemoveOption(idx, oIdx)}
                                                                                    className="h-9 w-9 rounded-xl hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center transition-colors shrink-0"
                                                                                    title="Delete option"
                                                                                >
                                                                                    <X className="h-4 w-4" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    ))}

                                                                    {/* "Other..." Option Display if active */}
                                                                    {q.hasOther && (
                                                                        <div className="flex items-center gap-3 bg-slate-50/70 p-2.5 rounded-xl border border-dashed border-slate-200">
                                                                            <div className="text-slate-400">
                                                                                {q.type === 'multiple_choice' ? <CircleDot className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
                                                                            </div>
                                                                            <span className="text-xs font-bold text-slate-500 italic flex-1">
                                                                                "Other..." (Allows patient to enter custom text)
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleToggleOther(idx)}
                                                                                className="h-7 w-7 rounded-lg hover:bg-red-100 text-red-500 flex items-center justify-center transition-colors"
                                                                                title="Remove Other"
                                                                            >
                                                                                <X className="h-3.5 w-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Add option action buttons */}
                                                                <div className="flex items-center gap-3 pt-2 text-xs">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleAddOption(idx)}
                                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-50 hover:bg-brand-100 text-brand-700 font-bold transition-all"
                                                                    >
                                                                        <Plus className="h-3.5 w-3.5" /> Add Option
                                                                    </button>
                                                                    {(q.type === 'multiple_choice' || q.type === 'checkbox') && !q.hasOther && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleToggleOther(idx)}
                                                                            className="text-slate-500 hover:text-brand-600 font-bold tracking-wide"
                                                                        >
                                                                            or <span className="underline decoration-slate-300">Add "Other"</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* LINEAR SCALE CONFIGURATION */}
                                                        {q.type === 'rating' && (
                                                            <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                                                                <div className="flex flex-wrap items-center gap-4">
                                                                    <div className="flex items-center gap-2 text-xs font-bold text-slate-600">
                                                                        <span>Scale:</span>
                                                                        <span className="bg-white px-2.5 py-1 rounded-lg border border-slate-200 font-black">1</span>
                                                                        <span>to</span>
                                                                        <select
                                                                            value={q.scaleMax || 5}
                                                                            onChange={(e) => handleQuestionFieldChange(idx, 'scaleMax', Number(e.target.value))}
                                                                            className="h-9 px-3 rounded-lg border border-slate-200 bg-white font-black text-xs outline-none"
                                                                        >
                                                                            <option value={5}>5 (Standard)</option>
                                                                            <option value={10}>10 (Expanded)</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                    <div>
                                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                                            Label for 1 (Low benchmark)
                                                                        </label>
                                                                        <Input 
                                                                            placeholder="e.g. Strongly Disagree / Severely Impaired"
                                                                            value={q.scaleMinLabel || ''}
                                                                            onChange={(e) => handleQuestionFieldChange(idx, 'scaleMinLabel', e.target.value)}
                                                                            className="h-10 rounded-xl bg-white border border-slate-200 text-xs font-medium"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                                                                            Label for {q.scaleMax || 5} (High benchmark)
                                                                        </label>
                                                                        <Input 
                                                                            placeholder="e.g. Strongly Agree / Age Appropriate"
                                                                            value={q.scaleMaxLabel || ''}
                                                                            onChange={(e) => handleQuestionFieldChange(idx, 'scaleMaxLabel', e.target.value)}
                                                                            className="h-10 rounded-xl bg-white border border-slate-200 text-xs font-medium"
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* Visual Scale Preview */}
                                                                <div className="pt-2 flex items-center justify-center gap-2">
                                                                    {Array.from({ length: q.scaleMax || 5 }, (_, i) => i + 1).map(num => (
                                                                        <div key={num} className="h-9 w-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-bold text-xs text-slate-500 shadow-sm">
                                                                            {num}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* SHORT ANSWER PREVIEW */}
                                                        {q.type === 'short_answer' && (
                                                            <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                                                <p className="text-xs text-slate-400 font-medium border-b border-slate-300 pb-1 w-48">
                                                                    Short-answer text field
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* PARAGRAPH PREVIEW */}
                                                        {q.type === 'paragraph' && (
                                                            <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                                                <p className="text-xs text-slate-400 font-medium">
                                                                    Long-answer text / multi-line clinical observations
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* YES / NO PREVIEW */}
                                                        {q.type === 'boolean' && (
                                                            <div className="flex gap-3">
                                                                <div className="px-5 py-2.5 rounded-xl bg-green-50 text-green-700 border border-green-200 text-xs font-black uppercase">
                                                                    Yes
                                                                </div>
                                                                <div className="px-5 py-2.5 rounded-xl bg-red-50 text-red-700 border border-red-200 text-xs font-black uppercase">
                                                                    No
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* DATE PREVIEW */}
                                                        {q.type === 'date' && (
                                                            <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200 flex items-center gap-3 text-slate-400 text-xs">
                                                                <Calendar className="h-4 w-4" />
                                                                <span>Month, day, year (Calendar date picker)</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* GOOGLE FORMS ACTION BAR (Duplicate, Delete, Required Switch) */}
                                                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                            {QUESTION_TYPES.find(t => t.id === q.type)?.description}
                                                        </div>

                                                        <div className="flex items-center gap-4">
                                                            {/* Duplicate Button */}
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); handleDuplicateQuestion(idx); }}
                                                                className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-xs font-bold p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                                                                title="Duplicate question"
                                                            >
                                                                <Copy className="h-4 w-4" />
                                                                <span className="hidden sm:inline">Duplicate</span>
                                                            </button>

                                                            {/* Delete Button */}
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); handleRemoveQuestion(idx); }}
                                                                className="flex items-center gap-1.5 text-slate-500 hover:text-red-600 text-xs font-bold p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                                                                title="Delete question"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                                <span className="hidden sm:inline">Delete</span>
                                                            </button>

                                                            <div className="h-5 w-px bg-slate-200" />

                                                            {/* Required Toggle */}
                                                            <label 
                                                                onClick={(e) => e.stopPropagation()} 
                                                                className="flex items-center gap-2 cursor-pointer select-none"
                                                            >
                                                                <span className="text-xs font-bold text-slate-700">Required</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleQuestionFieldChange(idx, 'required', !q.required)}
                                                                    className={`w-11 h-6 rounded-full transition-colors relative p-0.5 focus:outline-none ${
                                                                        q.required ? 'bg-brand-600' : 'bg-slate-300'
                                                                    }`}
                                                                >
                                                                    <div className={`w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${
                                                                        q.required ? 'translate-x-5' : 'translate-x-0'
                                                                    }`} />
                                                                </button>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Add Question Floating Tool Bar */}
                                <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <Button
                                            type="button"
                                            onClick={() => handleAddQuestion('multiple_choice')}
                                            className="h-11 px-5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-black text-xs uppercase tracking-wider shadow-sm"
                                        >
                                            <Plus className="mr-1.5 h-4 w-4" /> Add Question
                                        </Button>
                                    </div>

                                    {/* Quick add type buttons */}
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-1">
                                            Quick Add:
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleAddQuestion('multiple_choice')}
                                            className="px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-brand-50 border border-slate-200 text-slate-700 hover:text-brand-600 text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1"
                                        >
                                            <CircleDot className="h-3 w-3" /> Choices
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleAddQuestion('checkbox')}
                                            className="px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-brand-50 border border-slate-200 text-slate-700 hover:text-brand-600 text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1"
                                        >
                                            <CheckSquare className="h-3 w-3" /> Checkbox
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleAddQuestion('rating')}
                                            className="px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-brand-50 border border-slate-200 text-slate-700 hover:text-brand-600 text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1"
                                        >
                                            <Sliders className="h-3 w-3" /> Scale
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleAddQuestion('short_answer')}
                                            className="px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-brand-50 border border-slate-200 text-slate-700 hover:text-brand-600 text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1"
                                        >
                                            <Type className="h-3 w-3" /> Text
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleAddQuestion('boolean')}
                                            className="px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-brand-50 border border-slate-200 text-slate-700 hover:text-brand-600 text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1"
                                        >
                                            <ToggleLeft className="h-3 w-3" /> Yes/No
                                        </button>
                                    </div>
                                </div>

                                {/* Modal Bottom Submit Button */}
                                <Button 
                                    className="w-full h-16 rounded-2xl bg-brand-600 hover:bg-brand-700 text-white font-black text-sm uppercase tracking-widest shadow-premium transition-all mt-4" 
                                    onClick={handleCreateSubmit}
                                >
                                    Finalize & Release Template ({createQuestions.length} Items) <CheckCircle className="ml-3 h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
            )}
            {/* Clinical Patient Selection Modal */}
            {isPatientModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[100] flex items-center justify-center p-6">
                    <Card className="w-full max-w-xl shadow-premium border-0 rounded-[3rem] overflow-hidden bg-white">
                        <div className="p-10 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h2 className="text-3xl font-black text-slate-900 font-outfit tracking-tight uppercase">Select Patient</h2>
                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">Initialize {selectedClinicalAssessment?.title}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setPatientModalOpen(false)} className="h-10 w-10 bg-white shadow-sm border border-slate-100 rounded-xl hover:text-red-500 transition-colors"><X className="h-5 w-5" /></Button>
                        </div>
                        
                        <div className="p-10 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 mb-2">Assigned Clinical Profiles</p>
                            {assignedProfiles.length > 0 ? assignedProfiles.map(p => (
                                <button 
                                    key={p.id}
                                    onClick={() => confirmClinicalStart(p)}
                                    className="w-full flex items-center justify-between p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:border-brand-500 hover:bg-brand-50 transition-all group"
                                >
                                    <div className="text-left">
                                        <p className="font-black text-slate-900 group-hover:text-brand-600 uppercase font-outfit">{p.name}</p>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{p.diagnosis} • {p.age}y</p>
                                    </div>
                                    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-brand-600" />
                                </button>
                            )) : (
                                <div className="text-center py-10 opacity-50">
                                    <p className="font-bold text-slate-400">No patients assigned to your care.</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
